import React from 'react';
import { Link } from 'react-router-dom';
import './PrivacyPolicy.css';

function PrivacyPolicy() {
  return (
    <div className="privacy-policy-container">
      <h1>Privacy Policy</h1>
      <p><strong>Effective Date:</strong> January 1, 2025</p>
      <p><strong>Entity:</strong> Non-Target LLC ("PrivateIQ," "we," "us," or "our")</p>
      <p><strong>Websites:</strong> non-target.com (this policy host), privateiq.golf (service site)</p>
      <p><strong>iOS App:</strong> PrivateIQ (companion app)</p>
      <p><strong>Contact:</strong> <a href="mailto:support@privateIQ.golf">support@privateIQ.golf</a></p>
      <p><strong>Address:</strong> 5511 Parkcrest Drive, Suite 103, #1000, Austin, TX 78731, USA</p>

      <h2>1) Scope</h2>
      <p>
        This Privacy Policy explains how we collect, use, disclose, and protect information when you use
        (a) our websites (including non-target.com and privateiq.golf) and (b) our iOS companion app (the "App").
        The App is login-only for existing website users; no new accounts or in-app purchases are offered.
      </p>

      <h2>2) Information We Collect</h2>

      <h3>2.1 You provide</h3>
      <p>
        <strong>Account & Profile</strong> (created on the website): name, email, phone, zip, age range,
        marital status, number of children, household income band, current-club status, and other details
        you choose to provide; credentials are stored securely.
      </p>
      <p>
        <strong>User Content:</strong> messages, forum posts, uploads (e.g., scorecards or verification files).
      </p>
      <p>
        <strong>Support:</strong> emails and forms.
      </p>

      <h3>2.2 Collected automatically</h3>
      <p>
        <strong>Device/Usage:</strong> IP address, device and OS, app version, browser, language, timestamps,
        pages/screens viewed, referral/UTM, performance and crash logs.
      </p>
      <p>
        <strong>In-App Events:</strong> sign-in state, settings (e.g., IQ Intro toggle), error diagnostics.
      </p>

      <h3>2.3 With your permission (mobile)</h3>
      <ul>
        <li>Push notifications</li>
        <li>Camera/Photos/Files for uploads you choose</li>
      </ul>

      <h2>3) How We Use Data</h2>
      <ul>
        <li>Provide, maintain, and secure the Services (authentication, fraud prevention, debugging).</li>
        <li>Operate member features (message boards, messaging, IQ Intro).</li>
        <li>Communicate service notices and product updates.</li>
        <li>Analyze and improve performance and usability.</li>
        <li>Comply with law and enforce terms.</li>
      </ul>

      <h2>4) IQ Intro — How it works</h2>
      <p>
        <strong>Before you accept a club's request:</strong> To help clubs discover prospective members,
        we may show them limited non-direct identifiers about matched prospects—e.g., age range, income band
        (not exact income), household composition band (e.g., number of children range), proximity to the club,
        and whether you are a current club member. We do not disclose your name, email, or phone at this stage.
      </p>
      <p>
        <strong>Opt-out:</strong> You can turn this off at any time (see Section 8).
      </p>
      <p>
        <strong>When you accept a specific club's request:</strong> You authorize us to disclose your selected
        profile attributes and contact details (e.g., name, email, phone) to that club so they can contact you.
        The club may export accepted contacts to its CRM (e.g., Salesforce/HubSpot) for prospective-member
        outreach for that club. After disclosure, the club's own privacy policy applies to its independent
        processing; you may withdraw consent with the club directly or ask us to relay your request.
      </p>
      <p>
        <strong>Club obligations:</strong> Clubs are contractually prohibited from re-identifying users prior
        to acceptance, reselling/transferring data, or using IQ Intro data for unrelated marketing. We may
        audit access and suspend violators.
      </p>
      <p>
        <strong>Legal note:</strong> Showing user-level attributes to clubs before you accept may be considered
        a "sale" or "share" of personal information under some state laws, which gives you opt-out rights
        (see Section 8). California also requires honoring recognized Global Privacy Control (GPC) signals
        on the web. We follow those rules.
      </p>

      <h2>5) App Privacy (Apple)</h2>
      <p>
        We disclose our data collection (including any third-party SDKs used for analytics/crash reporting)
        in App Store Connect's App Privacy details and keep them current. If we ever engage in cross-app/site
        tracking, we will request Apple's App Tracking Transparency (ATT) permission and update our disclosures.
      </p>

      <h2>6) When We Share Data</h2>
      <p>
        <strong>Service Providers:</strong> cloud hosting, analytics/crash logs, customer support, email delivery,
        limited payment metadata (for website purchases), and fraud prevention—bound by contract.
      </p>
      <p>
        <strong>Clubs (IQ Intro):</strong> as described above, only limited attributes pre-acceptance;
        full contact only after your acceptance/authorization.
      </p>
      <p>
        <strong>Legal/Compliance and Business Transfers</strong> as customary.
      </p>
      <p>
        We do not sell personal information for money; some IQ Intro disclosures may be deemed a sale/share
        under state law (see Section 8).
      </p>

      <h2>7) Data Retention & Security</h2>
      <p>
        We retain personal data only as long as necessary for the purposes above (or longer as required by law),
        then delete or de-identify it. We use reasonable administrative, technical, and physical safeguards;
        no method is 100% secure.
      </p>

      <h2>8) Your Privacy Choices</h2>
      <p><strong>IQ Intro Opt-Out (Do Not Sell/Share):</strong></p>
      <ul>
        <li><strong>iOS App:</strong> Settings &rarr; Account Settings &rarr; Privacy Preferences &rarr; IQ Intro (toggle off).</li>
        <li><strong>Web:</strong> We provide a "<Link to="/privacy-choices">Your Privacy Choices</Link>" / "Do Not Sell or Share My Personal Information" link in our site footer.</li>
        <li><strong>GPC:</strong> On web, when we detect a valid Global Privacy Control signal, we treat it as an opt-out of sale/share for that session/account as required in some states.</li>
      </ul>
      <p>
        <strong>Access/Correct/Delete:</strong> Email <a href="mailto:support@privateIQ.golf">support@privateIQ.golf</a>.
      </p>
      <p>
        <strong>Notifications & Permissions:</strong> Manage in iOS Settings.
      </p>
      <p>
        We'll authenticate requests and respond as required by applicable law.
      </p>

      <h2>9) Account Deletion (In-App)</h2>
      <p>
        Even though accounts are created on the website, you can initiate deletion in-app at
        Settings &rarr; Account &rarr; Delete Account. We delete your profile and personal data we're not
        legally required to keep (e.g., tax/fraud records) and maintain a minimal suppression record to
        prevent unintended re-creation.
      </p>

      <h2>10) Children</h2>
      <p>
        Our Services are not directed to children under 13 (or older minimum ages where applicable).
        If we learn we collected data from a child, we'll delete it.
      </p>

      <h2>11) State-Specific Disclosures (summary)</h2>
      <p>
        <strong>California (CPRA):</strong> Right to access/correct/delete; opt-out of sale/share;
        right to limit use of sensitive personal information; GPC recognition on web.
        Provide or link to "Your Privacy Choices."
      </p>
      <p>
        <strong>Colorado (CPA):</strong> Opt-out of sale and targeted advertising; recognize an approved
        universal opt-out mechanism (GPC).
      </p>
      <p>
        <strong>Texas (TDPSA):</strong> Consumers may opt out of sale and targeted advertising;
        small-business considerations may apply; consent required to sell sensitive data.
      </p>
      <p>
        (We will update this section as new state laws take effect.)
      </p>

      <h2>12) Third-Party Links</h2>
      <p>
        Third-party sites/services have their own privacy policies and terms.
      </p>

      <h2>13) Changes to this Policy</h2>
      <p>
        We'll post updates here with a new Effective Date. For material changes, we may provide
        additional notice (e.g., in-app or email).
      </p>

      <h2>14) Contact</h2>
      <p>
        Questions or rights requests: <a href="mailto:support@privateIQ.golf">support@privateIQ.golf</a>
      </p>
      <address>
        <strong>Non-Target LLC</strong><br />
        5511 Parkcrest Drive, Suite 103, #1000<br />
        Austin, TX 78731, USA
      </address>
    </div>
  );
}

export default PrivacyPolicy;